import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

const testimonials = [
  {
    quote:
      'By making learning fun and rewarding, the BSP can help break down barriers and empower individuals to embrace Braille as a valuable tool for lifelong learning and independence.',
    role: 'Superintendent, Kansas State School for the Blind',
  },
  {
    quote:
      'Combines research, creation, innovation and forward-thinking solutions to address a need in the Braille instructional field.',
    role: 'Vocational Rehabilitation Specialist, Oregon Commission for the Blind',
  },
  {
    quote:
      'I and a number of teachers that I spoke with see great expansive potential in this novel idea of Braille introduction.',
    role: 'Career Education Instructor, Texas School for the Blind and Visually Impaired',
  },
  {
    quote:
      'The BSP’s innovative design and gamified approach align nicely with our mission to foster a love of learning through hands-on exploration and play.',

    role: 'Superintendent, Kansas State School for the Blind',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const Testimonials = () => {
  return (
    <section className="relative py-20 md:py-24 overflow-hidden">
      {/* soft gradient + blobs */}
      <div className="absolute inset-0 -z-10" style={{ background: 'var(--background-color)' }} />
      <div className="decorative-blob-1" />
      <div className="decorative-blob-2" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border" style={{ backgroundColor: 'var(--card-background-gradient)', color: 'var(--link-color)', borderColor: 'var(--card-border)' }}>
            Trusted by educators
          </span>
          <h2 className="mt-4 text-3xl md:text-6xl font-extrabold leading-tight" style={{ color: 'var(--home-text-primary)' }}>
            What People Are Saying
          </h2>
          <p className="mt-4 max-w-2xl mx-auto" style={{ color: 'var(--home-text-secondary)' }}>
            Educators and specialists on the Braille Skill Puzzle (BSP) and our play‑first approach.
          </p>
        </div>

        {/* grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={cardVariants}
              className="group relative rounded-3xl p-[1px] bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-transparent hover:from-blue-600/30 hover:via-purple-600/30 transition-colors"
            >
              {/* card body */}
              <blockquote className="relative backdrop-blur-md rounded-3xl p-6 md:p-8 border shadow-lg hover:shadow-2xl transition-all" style={{ backgroundColor: 'var(--home-card-bg)', borderColor: 'var(--home-card-border)' }}>

                <p className="text-lg md:text-xl leading-relaxed" style={{ color: 'var(--home-text-secondary)' }}>
                  “{t.quote}”
                </p>

                <footer className="mt-6 pt-6 border-t flex items-start gap-3" style={{ borderColor: 'var(--home-card-border)' }}>
                  <div className="flex-1">
                    <div className="text-sm font-bold" style={{ color: 'var(--home-text-secondary)' }}>{t.role}</div>
                  </div>
                  {/* subtle chevron accent on hover */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity translate-x-0 group-hover:translate-x-1 duration-300" style={{ color: 'var(--link-color)' }}>
                    →
                  </div>
                </footer>

                {/* hover ring */}
                <div className="absolute inset-0 rounded-3xl ring-1 ring-transparent group-hover:ring-blue-300/40 transition-all pointer-events-none" />
              </blockquote>
            </motion.div>
          ))}
        </div>

        {/* CTA stripe */}
        <div className="mt-12 md:mt-16">
          <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-r from-blue-600 to-purple-600 p-[1px]">
            <div className="rounded-2xl backdrop-blur-md px-6 py-6 md:px-10 md:py-8 flex flex-col md:flex-row items-center justify-between gap-4" style={{ backgroundColor: 'var(--home-card-bg)' }}>
              <div>
                <h3 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--home-text-primary)' }}>
                  Ready to bring play‑first Braille to your learners?
                </h3>
                <p className="mt-1" style={{ color: 'var(--home-text-secondary)' }}>
                  Join educators using Amaze Puzzles™ to build confidence and curiosity.
                </p>
              </div>
              <a
                href="/contact"
                className="btn-primary inline-flex items-center"
                aria-label="Contact Amaze Puzzles"
              >
                Contact Us
                <span aria-hidden>↗</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
